<script setup>
import { ref, computed, onMounted, onUnmounted } from "vue";

import Button from "@/shared/ui/button/Button.vue";
import { FormSheet } from "@/shared/ui/form-sheet";
import { FormItem, FormLabel, FormControl } from "@/shared/ui/form";
import { Input } from "@/shared/ui/input";
import { DatePicker } from "@/shared/ui/date-picker";
import { useInheritanceVerification } from "@/entities/inheritance/model/useInheritanceVerification";
import SvgIcon from "@/shared/ui/svg-icon/SvgIcon.vue";
import { InfoGroup } from "@/shared/ui/info-group";
import { Kbd } from "@/shared/ui/kbd";
import { useI18n } from "vue-i18n";

const props = defineProps({
  inheritanceId: { type: String, required: true },
  participantName: { type: String, default: "" },
  participantBirthDate: { type: String, default: "" },
});

const emit = defineEmits(["close"]);

const { t } = useI18n();
const { checkResults, isVerifying, runChecks } = useInheritanceVerification();

const testamentInfoGroups = computed(() => {
  const d = checkResults.value.testament?.data;
  if (!d) return [];

  const groups = [];

  const caseItems = [
    { label: t("ФИО умершего"), value: d.deceased_fio },
    { label: t("№ дела"), value: d.existing_nd_number },
    { label: t("Статус"), value: d.existing_nd_status },
  ].filter((i) => i.value);

  if (caseItems.length) {
    groups.push({ title: t("Данные дела"), items: caseItems });
  }

  const notary = d.notary;
  if (notary) {
    const notaryItems = [
      { label: t("Нотариус"), value: notary.fio },
      { label: t("Статус лицензии"), value: notary.license_status },
      { label: t("Телефон"), value: notary.phone },
      { label: t("Электронная почта"), value: notary.email },
      { label: t("Адрес"), value: notary.address },
    ].filter((i) => i.value);

    if (notaryItems.length) {
      groups.push({ title: t("Данные нотариуса"), items: notaryItems });
    }
  }

  const heirs = d.participants?.filter(
    (p) => p.participant_type_code !== "NASLEDODATEL"
  );
  if (heirs?.length) {
    groups.push({
      title: t("Наследники"),
      items: heirs.map((p, i) => ({
        label: `${t("Наследник")} ${i + 1}`,
        value: [p.last_name, p.first_name, p.middle_name]
          .filter(Boolean)
          .join(" "),
      })),
    });
  }

  return groups;
});

const visible = ref(false);

const form = ref({
  full_name: props.participantName || "",
  birth_date: props.participantBirthDate || "",
});

const errors = ref({});
const submitted = ref(false);

function validate() {
  const f = form.value;
  const e = {};
  if (!f.full_name?.trim()) e.full_name = true;
  else if (f.full_name.trim().split(/\s+/).length < 2) e.full_name = true;
  if (!f.birth_date) e.birth_date = true;
  errors.value = e;
  return Object.keys(e).length === 0;
}

async function handleCheck() {
  submitted.value = true;
  if (!validate()) return;

  const nameParts = form.value.full_name.trim().split(/\s+/);
  await runChecks(props.inheritanceId, {
    last_name: nameParts[0] || "",
    first_name: nameParts[1] || "",
    middle_name: nameParts[2] || "",
    birth_date: form.value.birth_date,
  });
}

function close() {
  visible.value = false;
  emit("close");
}

function onKeydown(e) {
  if (e.key === "Enter" && visible.value) {
    e.preventDefault();
    handleCheck();
  }
}

onMounted(() => {
  visible.value = true;
  window.addEventListener("keydown", onKeydown);
});

onUnmounted(() => {
  window.removeEventListener("keydown", onKeydown);
});
</script>

<template>
  <FormSheet
    v-model:open="visible"
    :title="$t('Поиск завещания по республике')"
    :description="$t('Наследодатель')"
    :width="480"
    @close="close"
  >
    <div class="w-full h-full flex flex-col gap-6">
      <div class="flex flex-col gap-4 py-2">
        <FormItem float>
          <FormLabel>{{ $t("ФИО умершего") }} *</FormLabel>
          <FormControl>
            <Input
              v-model="form.full_name"
              :class="{
                '!border-destructive': submitted && errors.full_name,
              }"
            />
          </FormControl>
        </FormItem>

        <FormItem float>
          <FormLabel>{{ $t("Дата рождения") }} *</FormLabel>
          <FormControl>
            <DatePicker
              v-model="form.birth_date"
              :placeholder="$t('гггг-мм-дд')"
              editable
              :class="{
                '!border-destructive': submitted && errors.birth_date,
              }"
            />
          </FormControl>
        </FormItem>

        <p class="text-xs text-muted-foreground flex items-center gap-1">
          {{ $t("Нажмите") }}
          <Kbd>Enter ↵</Kbd>
          {{ $t("для проверки") }}
        </p>
      </div>

      <div v-if="checkResults.testament" class="flex flex-col gap-3">
        <div
          v-if="checkResults.testament.found && testamentInfoGroups.length"
          class="p-3 rounded-lg border border-border"
        >
          <InfoGroup :groups="testamentInfoGroups" />
        </div>

        <div
          v-else
          class="flex items-center gap-3 p-3 rounded-lg bg-yellow-50 border border-yellow-200"
        >
          <svg-icon name="huge-warning" class="text-yellow-400 w-4 h-4" />

          <div class="text-foreground" style="font-size: 0.83rem">
            {{ checkResults.testament.message }}
          </div>
        </div>
      </div>
    </div>

    <template #footer>
      <div class="flex-1"></div>
      <Button variant="outline" @click="close">
        {{ $t("Отмена") }}
      </Button>
      <Button class="ml-2" @click="handleCheck" :disabled="isVerifying">
        {{ isVerifying ? $t("Проверка...") : $t("Проверить") }}
      </Button>
    </template>
  </FormSheet>
</template>
